import type { LucideIcon } from "lucide-react";
import {
  Clock, Car, Smartphone, MapPin, BarChart3, ShieldCheck,
  Navigation, Receipt, Gauge, FileText, Check,
  FolderKanban, Coins, PackagePlus, UserPlus, Route,
} from "lucide-react";
import { useLocale } from "../i18n/useLocale";
import { cmsImage } from "../i18n/cmsImage";
import { BoardMock, PhoneShot } from "./Mockups";

// Fallback stock photo until an editor uploads a per-block image in the CMS.
const WORKER = "https://framerusercontent.com/images/gvrZUoowU6t5rwpwtONkkVWoFSU.png";

// Screencast slot → a temporary dashboard mockup.
export function VisualPlaceholder({ label, ratio = "aspect-[4/3]" }: { label: string; ratio?: string }) {
  return (
    <div className={`relative ${ratio} overflow-hidden rounded-2xl border border-line bg-bg-subtle`}>
      <BoardMock />
      <span className="sr-only">{label}</span>
    </div>
  );
}

// Each feature gets a minimalist motion that mirrors what it does.
// NOTE (i18n, Story 4.7): the string fields below are NOT display copy — they are
// `features` namespace KEY SUFFIXES. `FeatureDetailBlock` resolves them through
// `t("features:" + suffix)`. Market-specific copy now lives per-locale in the
// catalogs (the `en` locale carries the international wording, `nl` the Belgian),
// so a single key per field resolves correctly without a component-level branch.
export type AnimKind = "tap" | "locate" | "bars" | "shield" | "route" | "plus";
export type Detail = { icon: LucideIcon; title: string; body: string; anim: AnimKind; tag?: string };
export type DetailBlock = { icon: LucideIcon; family: string; tag: string; title: string; lead: string; details: Detail[]; highlight: string; photo: string };

export const DETAIL_BLOCKS: DetailBlock[] = [
  {
    icon: Clock, family: "detail.time.family", tag: "detail.time.tag",
    title: "detail.time.title",
    lead: "detail.time.lead",
    photo: "detail.time.photo",
    details: [
      { icon: Smartphone, anim: "tap", title: "detail.time.checkin.title", body: "detail.time.checkin.body" },
      { icon: MapPin, anim: "locate", title: "detail.time.location.title", body: "detail.time.location.body" },
      { icon: BarChart3, anim: "bars", title: "detail.time.live.title", body: "detail.time.live.body" },
      { icon: ShieldCheck, anim: "shield", title: "detail.time.tamper.title", body: "detail.time.tamper.body" },
    ],
    highlight: "detail.time.highlight",
  },
  {
    icon: Car, family: "detail.vehicles.family", tag: "detail.vehicles.tag",
    title: "detail.vehicles.title",
    lead: "detail.vehicles.lead",
    photo: "detail.vehicles.photo",
    details: [
      { icon: Navigation, anim: "route", title: "detail.vehicles.mileage.title", body: "detail.vehicles.mileage.body" },
      { icon: Receipt, anim: "plus", title: "detail.vehicles.expenses.title", body: "detail.vehicles.expenses.body" },
      { icon: FileText, anim: "bars", title: "detail.vehicles.cost.title", body: "detail.vehicles.cost.body" },
      { icon: Gauge, anim: "locate", title: "detail.vehicles.track.title", body: "detail.vehicles.track.body", tag: "detail.vehicles.track.tag" },
    ],
    highlight: "detail.vehicles.highlight",
  },
  {
    icon: FolderKanban, family: "detail.projects.family", tag: "detail.projects.tag",
    title: "detail.projects.title",
    lead: "detail.projects.lead",
    photo: "detail.projects.photo",
    details: [
      { icon: Coins, anim: "bars", title: "detail.projects.cost.title", body: "detail.projects.cost.body" },
      { icon: PackagePlus, anim: "plus", title: "detail.projects.extra.title", body: "detail.projects.extra.body" },
      { icon: UserPlus, anim: "plus", title: "detail.projects.subcontractor.title", body: "detail.projects.subcontractor.body" },
      { icon: Route, anim: "route", title: "detail.projects.transport.title", body: "detail.projects.transport.body" },
    ],
    highlight: "detail.projects.highlight",
  },
];

// Minimalist looping motion that mirrors each feature, on a grey tile. One accent
// colour, subtle loop, reuses the shared ds-* keyframes. Themed by `kind`.
function MiniAnim({ kind }: { kind: AnimKind }) {
  return (
    <div className="relative grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-xl bg-bg-subtle text-ds-accent">
      {/* tap → a single tap with a ripple ping (one-tap check-in) */}
      {kind === "tap" && (
        <span className="relative flex h-7 w-7 items-center justify-center">
          <span className="ds-locate absolute inline-flex h-full w-full rounded-full bg-ds-accent" />
          <span className="relative h-2.5 w-2.5 rounded-full bg-ds-accent" />
        </span>
      )}

      {/* locate → radar rings pinging out from a point (location / track & trace) */}
      {kind === "locate" && (
        <span className="relative grid h-8 w-8 place-items-center">
          <span className="ds-locate absolute h-full w-full rounded-full border-2 border-ds-accent" />
          <span className="ds-locate absolute h-full w-full rounded-full border-2 border-ds-accent" style={{ animationDelay: "1.5s" }} />
          <span className="relative h-2 w-2 rounded-full bg-ds-accent" />
        </span>
      )}

      {/* bars → live equalizer (live attendance / live cost) */}
      {kind === "bars" && (
        <span className="flex h-7 items-end gap-1">
          <span className="ds-bar h-7 w-1.5 rounded-full bg-ds-accent" style={{ animationDelay: "0s" }} />
          <span className="ds-bar h-7 w-1.5 rounded-full bg-ds-accent" style={{ animationDelay: "0.22s" }} />
          <span className="ds-bar h-7 w-1.5 rounded-full bg-ds-accent" style={{ animationDelay: "0.44s" }} />
          <span className="ds-bar h-7 w-1.5 rounded-full bg-ds-accent" style={{ animationDelay: "0.14s" }} />
        </span>
      )}

      {/* shield → a check drawing itself inside a shield (tamper-proof) */}
      {kind === "shield" && (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden className="h-9 w-9">
          <path d="M12 2.5 5 5.2v5.5c0 4.6 3 7.6 7 9.3 4-1.7 7-4.7 7-9.3V5.2L12 2.5Z" stroke="var(--ds-accent)" strokeWidth="1.6" strokeLinejoin="round" opacity="0.4" />
          <path d="M8.6 12.2 11 14.6l4.4-4.7" stroke="var(--ds-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" pathLength={100} className="ds-circle-draw" />
        </svg>
      )}

      {/* route → a path plotting itself between two points (mileage / transport) */}
      {kind === "route" && (
        <svg viewBox="0 0 40 32" fill="none" aria-hidden className="h-8 w-10">
          <path d="M7 25c8 0 5-16 14-16 5 0 8-2 12-2" stroke="var(--ds-accent)" strokeWidth="2.2" strokeLinecap="round" pathLength={100} className="ds-route-draw" />
          <circle cx="7" cy="25" r="2.6" fill="var(--ds-accent)" />
          <circle cx="33" cy="7" r="2.6" fill="var(--ds-accent)" />
        </svg>
      )}

      {/* plus → an "add" that gently pops (expenses / extra work / subcontractors) */}
      {kind === "plus" && (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden className="ds-pop h-7 w-7">
          <path d="M12 5v14M5 12h14" stroke="var(--ds-accent)" strokeWidth="2.4" strokeLinecap="round" />
        </svg>
      )}
    </div>
  );
}

const GoldTag = ({ children }: { children: string }) => (
  <span className="inline-flex items-center rounded-full bg-ds-gold px-3 py-1 text-[13px] font-semibold text-ds-ink">{children}</span>
);

// Large square slot: photo → a per-block CMS image (WORKER fallback), video → a
// phone mockup (for now).
function VisualSquare({ kind, label, src, phone = "running" }: { kind: "photo" | "video"; label: string; src?: string; phone?: "running" | "idle" }) {
  return (
    <div className="relative aspect-square overflow-hidden rounded-2xl border border-line bg-white">
      {kind === "photo"
        ? <img src={src ?? WORKER} alt={label} loading="lazy" decoding="async" className="h-full w-full object-cover" />
        : <div className="flex h-full w-full items-center justify-center p-6"><PhoneShot state={phone} /></div>}
    </div>
  );
}

export function FeatureDetailBlock({ block }: { block: DetailBlock }) {
  const { t } = useLocale();
  const tf = (suffix: string) => t(`features:${suffix}`);
  return (
    <div className="rounded-3xl border border-line bg-bg-subtle p-4 shadow-[0_1px_3px_rgba(15,23,42,0.05)] md:p-10">
      {/* Yellow family + tier tags (replacing the icon) */}
      <div className="flex flex-wrap items-center gap-2">
        <GoldTag>{tf(block.family)}</GoldTag>
        <GoldTag>{tf(block.tag)}</GoldTag>
      </div>
      <div className="mt-6 grid gap-3 md:grid-cols-[1.1fr_0.9fr] md:items-end">
        <h3 className="font-display text-[30px] font-semibold leading-tight tracking-tight text-ds-ink md:text-[36px]">{tf(block.title)}</h3>
        <p className="text-ds-18 leading-relaxed text-ds-ink-soft">{tf(block.lead)}</p>
      </div>

      {/* Large photo / video blocks — 2 on web & tablet, 1 on mobile */}
      <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <VisualSquare kind="photo" src={cmsImage(t, `features:${block.photo}`, WORKER)} label={t("features:detail.media.photo")} />
        {/* Only the time block shows a running timer — the others (vehicles &
            co.) show the idle screen, so a page of blocks isn't five identical
            phones all counting the same session. */}
        <VisualSquare kind="video" label={t("features:detail.media.walkthrough")} phone={block.family === "detail.time.family" ? "running" : "idle"} />
      </div>

      {/* Each feature → its own white block (square animation + DS-consistent text) */}
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {block.details.map((d) => (
          <div key={d.title} className="rounded-2xl bg-white p-5 shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
            <MiniAnim kind={d.anim} />
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <h4 className="text-ds-18 font-semibold text-ds-ink">{tf(d.title)}</h4>
              {d.tag && <span className="rounded-full bg-bg-subtle px-2 py-0.5 text-[11px] font-semibold text-ink-faint">{tf(d.tag)}</span>}
            </div>
            <p className="mt-1.5 text-ds-14 leading-relaxed text-ds-ink-soft">{tf(d.body)}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-3 rounded-2xl bg-white px-6 py-4 shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
        <Check size={18} strokeWidth={2.4} className="shrink-0 text-success" />
        <p className="text-ds-14 font-medium text-ds-ink">{tf(block.highlight)}</p>
      </div>
    </div>
  );
}
